import { Link, Badge } from "ui";

export const Provider = ({
  provider,
}: {
  provider: { icon: string; name: string; id: string };
}) => {
  return (
    <div className="flex items-center space-x-4">
      <img
        className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg"
        src={provider.icon || ""}
        alt=""
      />
      <div className="max-w-[190px]">
        <p
          title={provider.name || undefined}
          className="truncate pr-4 text-sm text-foundations-subtle"
        >
          {provider.name}
        </p>
        <p className="text-xs text-foundations-subtler">{provider.id}</p>
      </div>
    </div>
  );
};

export const Account = ({
  account,
}: {
  account: { name: string; id: string };
}) => (
  <Link href={`/settings/accounts/${account.id}`} target="_blank">
    <div className="flex items-center space-x-1">
      <div className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-foundations-bold text-xs font-medium uppercase text-foundations-inverse">
        {account.name.slice(0, 2)}
      </div>
      <div>{account.name}</div>
    </div>
  </Link>
);
export const PaymentMethod = ({
  method,
}: {
  method: { logo: string; method_name: string };
}) =>
  method ? (
    <div className="flex items-center space-x-4">
      <img
        className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg"
        src={method.logo || ""}
        alt=""
      />
    </div>
  ) : null;

type ConnectionRow = {
  id: string;
  name: string;
  status: "ACTIVE" | "NOT_ACTIVE";
  provider: {
    id: string;
    icon: string;
    name: string;
  };
  payment_methods: {
    logo: string;
    method_name: string;
  }[];
  payout_methods: {
    logo: string;
    method_name: string;
  }[];
  accounts: {
    id: string;
    name: string;
  }[];
};

export const customConnectionHeadings: Partial<
  Record<keyof ConnectionRow, string>
> = {
  payment_methods: "Methods & Services",
};

export const customConnectionRender: Partial<
  Record<
    keyof ConnectionRow,
    (_props: ConnectionRow) => JSX.Element | undefined | null
  >
> = {
  provider: ({ provider }) => provider && <Provider provider={provider} />,
  accounts: ({ accounts }) => (
    <div className="flex items-center">
      <Account account={accounts[0]} />
      {accounts.length > 1 ? (
        <span className="inline-flex pl-1 text-foundations-boldest">
          +{accounts.length - 1}
        </span>
      ) : null}
    </div>
  ),
  payment_methods: ({ payment_methods, payout_methods, provider }) => {
    if (provider.name === "Forter") {
      return <Badge size="sm">Fraud Detection</Badge>;
    }

    if (provider.name === "Shield") {
      return <Badge size="sm">Device Check</Badge>;
    }

    const methods = payment_methods.concat(payout_methods);
    return (
      <div className="flex items-center">
        <PaymentMethod method={methods[0]} />
        {methods.length > 1 ? (
          <span className="inline-flex pl-1 text-foundations-boldest">
            +{methods.length - 1}
          </span>
        ) : null}
      </div>
    );
  },
  payout_methods: ({ payout_methods }) => (
    <div className="flex items-center">
      <PaymentMethod method={payout_methods[0]} />

      {payout_methods.length > 1 ? (
        <span className="inline-flex pl-1 text-foundations-boldest">
          +{payout_methods.length - 1}
        </span>
      ) : null}
    </div>
  ),
  status: ({ status }) => (
    <Badge variant={status === "ACTIVE" ? "success" : "default"}>
      {status}
    </Badge>
  ),
};

export const customColumns = [
  "id",
  "name",
  "status",
  "provider",
  "payment_methods",
  "accounts",
];
